import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import axios from 'axios';
import {
  Loader2, Save, Eye, Send, AlertTriangle, CalendarClock, Ban, CheckCircle2,
  Users, Sparkles, Wand2, Plus, RefreshCw, FileText, Trash2, ArrowUp, ArrowDown, Newspaper, Star,
} from 'lucide-react';
import { EmailImagePanel } from './EmailImagePanel';
import VideoCandidatesNote from './VideoCandidatesNote';
import IssueNotices, { statusLabel } from './IssueNotices';

const API = '/api'; // Same-origin: /api routes to backend on the serving domain.

// The two newsletter streams. Audience is auto-bound to the stream (display-only).
const STREAMS = [
  { type: 't2t', label: 'Teacher to Teacher', audience: 'Certified fitters & teaching pros' },
  { type: 'customer', label: 'Customer Newsletter', audience: 'Golfers & friends of HG' },
];

// Standing T2T Fitter Resource — the Shaft Reference Guide PDF (Cloudinary).
// Pre-fills the Fitter Resource block; still editable per issue.
const SHAFT_GUIDE_URL = 'https://res.cloudinary.com/dflzi5xc3/image/upload/HG_Shaft_Reference_Guide_1_eibctl.pdf';

const todayISO = () => new Date().toISOString().slice(0, 10);

const newsSlot = () => ({ kind: 'news', title: "What's New", copy: '', image: null });

const emptyIssue = (type) => ({
  id: null,
  type,
  subject: '',
  preheader: '',
  issue_label: '',
  date_label: '',
  issue_date: todayISO(),
  intro: '',
  hero_image: null,
  sections: [newsSlot()],
  sign_off_lead: '',
  sign_off: 'Henry-Griffitts',
  status: 'draft',
  test_sent_at: null,
  last_test_campaign_id: null,
  scheduled_at: null,
  mailerlite_campaign_id: null,
  scheduled_test: false,
});

function Field({ label, hint, children }) {
  return (
    <div className="space-y-1.5">
      <div className="flex items-baseline justify-between">
        <label className="text-xs font-sans font-medium uppercase tracking-wider text-hg-text-dim">{label}</label>
        {hint && <span className="text-[10px] text-hg-text-dim font-sans">{hint}</span>}
      </div>
      {children}
    </div>
  );
}

const inputCls =
  'w-full bg-hg-bg border border-hg-border rounded-sm px-3 py-2 text-sm font-sans text-hg-text focus:border-hg-green focus:outline-none';

const KIND_LABEL = { news: "What's New", standard: 'Body Section', fitter_resource: 'Fitter Resource', review_request: 'Review Request' };

// One typed section block in the body editor.
function SectionBlock({ section, index, isFirst, isLast, streamLabel, onField, onImage, onMove, onRemove }) {
  const isNews = section.kind === 'news';
  const isResource = section.kind === 'fitter_resource';
  const isReview = section.kind === 'review_request';
  const isStandard = !isNews && !isResource && !isReview;
  return (
    <div className="border border-hg-border rounded-sm p-4 space-y-3 bg-hg-bg/40">
      <div className="flex items-center justify-between gap-2">
        <span className="inline-flex items-center gap-1.5 text-[11px] font-sans uppercase tracking-wider text-hg-green">
          {isNews ? <Newspaper size={12} /> : isResource ? <FileText size={12} /> : isReview ? <Star size={12} /> : null}
          {KIND_LABEL[section.kind] || 'Section'}
          {isNews && <span className="text-hg-text-dim normal-case tracking-normal">· always sent</span>}
        </span>
        <div className="flex items-center gap-1">
          {!isNews && (
            <>
              <button data-testid={`nlc-section-up-${index}`} onClick={() => onMove(index, -1)} disabled={isFirst}
                title="Move up" className="p-1 text-hg-text-dim hover:text-hg-green disabled:opacity-30 disabled:cursor-not-allowed">
                <ArrowUp size={13} />
              </button>
              <button data-testid={`nlc-section-down-${index}`} onClick={() => onMove(index, 1)} disabled={isLast}
                title="Move down" className="p-1 text-hg-text-dim hover:text-hg-green disabled:opacity-30 disabled:cursor-not-allowed">
                <ArrowDown size={13} />
              </button>
              <button data-testid={`nlc-section-remove-${index}`} onClick={() => onRemove(index)}
                title="Remove section" className="p-1 text-hg-text-dim hover:text-red-400">
                <Trash2 size={13} />
              </button>
            </>
          )}
        </div>
      </div>

      <Field label="Title" hint={isNews ? 'defaults to “What’s New”' : undefined}>
        <input className={inputCls} data-testid={`nlc-section-title-${index}`}
          value={section.title || ''} onChange={(e) => onField(index, 'title', e.target.value)} />
      </Field>
      <Field label="Body">
        <textarea rows={isNews ? 3 : 5} className={inputCls} data-testid={`nlc-section-copy-${index}`}
          value={section.copy || ''} onChange={(e) => onField(index, 'copy', e.target.value)} />
      </Field>

      {isResource && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <Field label="Resource link URL">
            <input className={inputCls} data-testid={`nlc-section-resource-url-${index}`}
              value={section.resource_url || ''} onChange={(e) => onField(index, 'resource_url', e.target.value)}
              placeholder="https://…/shaft-reference-guide.pdf" />
          </Field>
          <Field label="Button label">
            <input className={inputCls} data-testid={`nlc-section-resource-label-${index}`}
              value={section.resource_label || ''} onChange={(e) => onField(index, 'resource_label', e.target.value)}
              placeholder="Download the guide" />
          </Field>
        </div>
      )}

      {isReview && (
        <>
          <Field label="Button label">
            <input className={inputCls} data-testid={`nlc-section-button-text-${index}`}
              value={section.button_text || ''} onChange={(e) => onField(index, 'button_text', e.target.value)}
              placeholder="Leave a review" />
          </Field>
          <p className="text-[10px] text-hg-text-dim font-sans flex items-center gap-1">
            <Star size={11} /> The button links to the public review form automatically, signed for
            this stream ({streamLabel}). No URL to set — it carries the audience + this issue as provenance.
          </p>
        </>
      )}

      {/* Optional CTA button on a standard section (e.g. a product callout). */}
      {isStandard && (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Field label="Button label" hint="optional">
              <input className={inputCls} data-testid={`nlc-section-button-text-${index}`}
                value={section.button_text || ''} onChange={(e) => onField(index, 'button_text', e.target.value)}
                placeholder="Learn more" />
            </Field>
            <Field label="Button URL" hint="optional — shows a button when set">
              <input className={inputCls} data-testid={`nlc-section-button-url-${index}`}
                value={section.button_url || ''} onChange={(e) => onField(index, 'button_url', e.target.value)}
                placeholder="https://…" />
            </Field>
          </div>
          <Field label="Image link" hint="optional — makes the image clickable (e.g. a video thumbnail → Vimeo)">
            <input className={inputCls} data-testid={`nlc-section-image-link-${index}`}
              value={section.image_link || ''} onChange={(e) => onField(index, 'image_link', e.target.value)}
              placeholder="https://vimeo.com/…" />
          </Field>
          <Field label="Image description (alt text)" hint="optional — what the image shows, for readers who cannot see it. Blank uses the section title.">
            <input className={inputCls} data-testid={`nlc-section-image-alt-${index}`}
              value={section.image_alt || ''} onChange={(e) => onField(index, 'image_alt', e.target.value)} />
          </Field>
        </>
      )}

      {/* Per-section image — not shown for the review CTA (it's a button block). */}
      {!isReview && (
        <EmailImagePanel
          testIdPrefix={`nlc-section-image-${index}`}
          imageData={section.image}
          onImageSet={(data) => onImage(index, data)}
          context="newsletter"
          promptContext={section.title || ''}
          label="Section image (optional)"
          size="compact"
          brandToggle
        />
      )}
    </div>
  );
}

export default function NewsletterComposer({ onNavigate }) {
  // `?module=newsletter&issue=<id>[&stream=customer]` opens that exact issue instead of the
  // stream's most recently edited one — so a report or a dashboard row can link to the draft
  // it is talking about, not to whichever draft happens to be newest.
  const deepLink = useMemo(() => {
    try {
      const p = new URLSearchParams(window.location.search);
      return { issue: p.get('issue') || '', stream: p.get('stream') || '' };
    } catch { return { issue: '', stream: '' }; }
  }, []);
  const deepUsed = useRef(false);
  const [streamType, setStreamType] = useState(() => (deepLink.stream === 'customer' ? 'customer' : 't2t'));
  const [issue, setIssue] = useState(() => emptyIssue('t2t'));
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [previewHtml, setPreviewHtml] = useState('');
  const [previewing, setPreviewing] = useState(false);
  const [notice, setNotice] = useState('');

  // Workflow / scheduling state (mirrors WeeklyFitterComposer).
  const [sendingTest, setSendingTest] = useState(false);
  const [scheduling, setScheduling] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [showSchedule, setShowSchedule] = useState(false);
  const [sendAt, setSendAt] = useState('');
  const [confirmChecked, setConfirmChecked] = useState(false);
  const [scheduleToTest, setScheduleToTest] = useState(false);
  const [target, setTarget] = useState(null);

  const [drafts, setDrafts] = useState([]);
  const [aiBrief, setAiBrief] = useState('');
  const [aiUpdate, setAiUpdate] = useState('');
  const [aiDrafting, setAiDrafting] = useState(false);
  const [batchCount, setBatchCount] = useState(4);
  const [batchThemes, setBatchThemes] = useState('');
  const [batchGenerating, setBatchGenerating] = useState(false);
  const [testEmail, setTestEmail] = useState('randall@henry-griffitts.com');

  const stream = STREAMS.find((s) => s.type === streamType) || STREAMS[0];

  const flash = (msg) => {
    setNotice(msg);
    setTimeout(() => setNotice(''), 4000);
  };

  const fetchDrafts = useCallback(async (type) => {
    try {
      const res = await axios.get(`${API}/email/newsletter/list?type=${type}`);
      setDrafts(res.data.issues || []);
    } catch { /* convenience list; ignore */ }
  }, []);

  // Load latest issue + target + drafts for the active stream.
  const loadStream = useCallback(async (type) => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/email/newsletter/latest?type=${type}`);
      setIssue(res.data.issue ? { ...emptyIssue(type), ...res.data.issue } : emptyIssue(type));
    } catch (e) {
      console.error('load newsletter failed', e);
      setIssue(emptyIssue(type));
    }
    // A deep-linked issue wins over "latest", once. Not found is not an error: the composer
    // simply stays on the latest issue, which is what it showed before links existed.
    if (deepLink.issue && !deepUsed.current) {
      deepUsed.current = true;
      try {
        const all = await axios.get(`${API}/email/newsletter/list?type=${type}`);
        const hit = (all.data.issues || []).find((d) => d.id === deepLink.issue);
        if (hit) setIssue({ ...emptyIssue(type), ...hit });
      } catch { /* keep the latest issue */ }
    }
    try {
      const t = await axios.get(`${API}/email/newsletter/target-info?type=${type}`);
      setTarget(t.data);
    } catch { setTarget(null); }
    await fetchDrafts(type);
    setPreviewHtml('');
    setShowSchedule(false);
    setLoading(false);
  }, [fetchDrafts, deepLink.issue]);

  useEffect(() => { loadStream(streamType); }, [streamType, loadStream]);

  // Any content edit invalidates a prior test and cancels a queued send (mirrors
  // the server-side guardrail) so a fresh test is always required before sending.
  const autoCancelScheduled = async (id) => {
    try {
      await axios.post(`${API}/email/newsletter/cancel-schedule?issue_id=${encodeURIComponent(id)}`);
      flash('Content changed — scheduled send cancelled. Re-test, then reschedule.');
    } catch {
      flash('Edited a scheduled issue — could not auto-cancel; check MailerLite.');
    }
  };

  // Apply content invalidation to a next-issue object (pure).
  const invalidate = (cur, next) => {
    if (cur.status === 'scheduled') {
      next.status = 'draft';
      next.scheduled_at = null;
      next.mailerlite_campaign_id = null;
      next.scheduled_test = false;
      next.test_sent_at = null;
      next.last_test_campaign_id = null;
    } else if (cur.test_sent_at) {
      next.test_sent_at = null;
      next.last_test_campaign_id = null;
      if (cur.status === 'test_sent') next.status = 'draft';
    }
    return next;
  };

  // Mutate a top-level backbone field with content invalidation.
  const setField = (k, v) => {
    if (issue[k] === v) return;
    if (issue.status === 'scheduled' && issue.mailerlite_campaign_id) autoCancelScheduled(issue.id);
    setIssue((cur) => invalidate(cur, { ...cur, [k]: v }));
  };

  // --- Section mutations (all count as content edits) ---
  const mutateSections = (fn) => {
    if (issue.status === 'scheduled' && issue.mailerlite_campaign_id) autoCancelScheduled(issue.id);
    setIssue((cur) => invalidate(cur, { ...cur, sections: fn(cur.sections) }));
  };

  const setSectionField = (idx, key, val) =>
    mutateSections((secs) => secs.map((s, i) => (i === idx ? { ...s, [key]: val } : s)));

  const setSectionImage = (idx, data) =>
    mutateSections((secs) => secs.map((s, i) => (i === idx ? { ...s, image: data } : s)));

  const addStandard = () =>
    mutateSections((secs) => [...secs, { kind: 'standard', title: '', copy: '', image: null }]);

  const addResource = () =>
    mutateSections((secs) => [...secs, {
      kind: 'fitter_resource', title: 'Shaft Reference Guide', copy: '', image: null,
      resource_url: SHAFT_GUIDE_URL, resource_label: 'Download the guide',
    }]);

  const addReviewRequest = () =>
    mutateSections((secs) => [...secs, {
      kind: 'review_request',
      title: "We'd love your feedback",
      copy: 'Your experience helps others find us. It only takes a minute.',
      button_text: 'Leave a review',
    }]);

  const removeSection = (idx) => mutateSections((secs) => secs.filter((_, i) => i !== idx));

  // Move a non-news section; the news slot stays pinned at index 0.
  const moveSection = (idx, dir) => mutateSections((secs) => {
    const target = idx + dir;
    if (target < 1 || target >= secs.length) return secs; // never above the news slot
    const next = [...secs];
    [next[idx], next[target]] = [next[target], next[idx]];
    return next;
  });

  const hasResource = issue.sections.some((s) => s.kind === 'fitter_resource');
  const hasReviewRequest = issue.sections.some((s) => s.kind === 'review_request');

  const save = async () => {
    setSaving(true);
    try {
      const payload = { ...issue, type: streamType };
      if (!payload.id) delete payload.id;
      const res = await axios.post(`${API}/email/newsletter/save`, payload);
      setIssue({ ...emptyIssue(streamType), ...res.data.issue });
      fetchDrafts(streamType);
      flash('Saved');
      return res.data.issue;
    } catch (e) {
      flash(e.response?.data?.detail || 'Save failed');
      return null;
    } finally {
      setSaving(false);
    }
  };

  const preview = async () => {
    setPreviewing(true);
    try {
      const res = await axios.post(`${API}/email/newsletter/preview`, { ...issue, type: streamType });
      setPreviewHtml(res.data.html || '');
    } catch { flash('Preview failed'); }
    setPreviewing(false);
  };

  const refreshLatest = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/email/newsletter/latest?type=${streamType}`);
      if (res.data.issue) setIssue({ ...emptyIssue(streamType), ...res.data.issue });
    } catch { /* keep current state */ }
    fetchDrafts(streamType);
  }, [streamType, fetchDrafts]);

  const openDraft = (d) => {
    setIssue({ ...emptyIssue(streamType), ...d });
    setPreviewHtml('');
    setShowSchedule(false);
    flash(`Opened: ${d.subject || '(untitled)'}`);
  };

  const newBlank = () => {
    setIssue(emptyIssue(streamType));
    setPreviewHtml('');
    setShowSchedule(false);
    setAiBrief('');
    flash('New blank issue');
  };

  const aiDraft = async () => {
    setAiDrafting(true);
    try {
      const res = await axios.post(`${API}/email/newsletter/ai-draft`, {
        type: streamType, brief: aiBrief || null, company_update: aiUpdate || null,
      });
      const f = res.data.fields || {};
      setIssue((cur) => ({
        ...cur,
        subject: f.subject ?? cur.subject,
        intro: f.intro ?? cur.intro,
        sections: f.sections?.length ? f.sections : cur.sections,
        // Generated content is unsent — reset workflow so it must be tested first.
        status: 'draft', test_sent_at: null, last_test_campaign_id: null,
        scheduled_at: null, mailerlite_campaign_id: null, scheduled_test: false,
      }));
      flash('AI draft ready — review & edit, then test.');
    } catch (e) {
      flash(e.response?.data?.detail || 'AI draft failed');
    }
    setAiDrafting(false);
  };

  const runBatch = async () => {
    setBatchGenerating(true);
    try {
      const res = await axios.post(`${API}/email/newsletter/ai-batch`, {
        type: streamType, count: batchCount, themes: batchThemes || null,
      });
      await fetchDrafts(streamType);
      const n = res.data.created || 0;
      flash(`Generated ${n} draft${n === 1 ? '' : 's'} — pick one below to review, edit & test.`);
    } catch (e) {
      flash(e.response?.data?.detail || 'Batch generation failed');
    }
    setBatchGenerating(false);
  };

  // datetime-local -> ISO with the user's local offset (mirrors WeeklyFitterComposer).
  const toOffsetISO = (local) => {
    const d = new Date(local);
    const pad = (n) => String(Math.abs(n)).padStart(2, '0');
    const tzMin = -d.getTimezoneOffset();
    const sign = tzMin >= 0 ? '+' : '-';
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}` +
      `T${pad(d.getHours())}:${pad(d.getMinutes())}:00` +
      `${sign}${pad(Math.trunc(Math.abs(tzMin) / 60))}:${pad(Math.abs(tzMin) % 60)}`;
  };

  const sendTest = async () => {
    const rcpt = (testEmail || '').trim();
    setSendingTest(true);
    try {
      const saved = await save();
      const id = saved?.id || issue.id;
      if (!id) { flash('Save first'); setSendingTest(false); return; }
      const q = `issue_id=${encodeURIComponent(id)}` + (rcpt ? `&recipient_email=${encodeURIComponent(rcpt)}` : '');
      await axios.post(`${API}/email/newsletter/send-test?${q}`);
      await refreshLatest();
      flash(rcpt ? `Test sent to ${rcpt} (via HG Hub Test group).` : 'Test sent to the HG Hub Test group.');
    } catch (e) {
      flash(e.response?.data?.detail || 'Test send failed');
    }
    setSendingTest(false);
  };

  const doSchedule = async () => {
    if (!sendAt) { flash('Pick a date & time'); return; }
    if (!confirmChecked) { flash('Confirm the send first'); return; }
    if (new Date(sendAt) <= new Date()) { flash('Pick a time in the future'); return; }
    setScheduling(true);
    try {
      const saved = await save();
      const id = saved?.id || issue.id;
      if (!id) { flash('Save first'); setScheduling(false); return; }
      const res = await axios.post(`${API}/email/newsletter/schedule`, {
        issue_id: id, send_at: toOffsetISO(sendAt), confirm: true, test: scheduleToTest,
      });
      setIssue((cur) => ({
        ...cur,
        status: 'scheduled',
        scheduled_at: res.data.scheduled_at,
        scheduled_test: !!res.data.test,
        scheduled_group_name: res.data.group_name,
        mailerlite_campaign_id: res.data.campaign_id,
      }));
      setShowSchedule(false);
      setConfirmChecked(false);
      setScheduleToTest(false);
      flash(scheduleToTest
        ? 'Test schedule created — see it on the Content Calendar.'
        : `Scheduled to the ${stream.label} list — see it on the Content Calendar.`);
    } catch (e) {
      flash(e.response?.data?.detail || 'Schedule failed');
    }
    setScheduling(false);
  };

  const cancelSchedule = async () => {
    if (!issue.id) return;
    if (!window.confirm('Cancel the scheduled send and return this issue to draft?')) return;
    setCancelling(true);
    try {
      await axios.post(`${API}/email/newsletter/cancel-schedule?issue_id=${encodeURIComponent(issue.id)}`);
      await refreshLatest();
      flash('Scheduled send cancelled — back to draft.');
    } catch (e) {
      flash(e.response?.data?.detail || 'Cancel failed');
    }
    setCancelling(false);
  };

  const status = issue.status || 'draft';
  const hasTested = !!issue.test_sent_at;
  const isScheduled = status === 'scheduled';
  const targetName = target?.group_name || `HG Newsletter — ${stream.label}`;
  const fmt = (s) => { try { return new Date(s).toLocaleString(); } catch { return s; } };
  const draftStatus = statusLabel;
  const mlUrl = issue.mailerlite_campaign_id
    ? `https://dashboard.mailerlite.com/campaigns/${issue.mailerlite_campaign_id}/preview`
    : null;
  const scheduledTargetName = issue.scheduled_test ? 'HG Hub Test (just you)' : targetName;

  // Index of the last non-news section (for move-down disabling).
  const lastIdx = issue.sections.length - 1;

  return (
    <div className="space-y-6">
      {/* Stream selector — audience is bound to the stream (display-only). */}
      <div className="bg-hg-surface border border-hg-border rounded-sm p-4 flex flex-wrap items-center gap-3">
        <span className="text-xs font-sans uppercase tracking-wider text-hg-text-dim">Stream</span>
        <div className="flex gap-2">
          {STREAMS.map((s) => (
            <button key={s.type} data-testid={`nlc-stream-${s.type}`} onClick={() => setStreamType(s.type)}
              className={`px-4 py-2 text-sm font-sans rounded-sm border transition-colors ${
                streamType === s.type ? 'border-hg-green bg-hg-green/10 text-hg-green' : 'border-hg-border text-hg-text-dim hover:border-hg-green/50'
              }`}>
              {s.label}
            </button>
          ))}
        </div>
        <span className="ml-auto inline-flex items-center gap-1.5 text-[11px] font-sans text-hg-text-dim">
          <Users size={12} /> Audience: <strong className="text-hg-text">{stream.audience}</strong>
        </span>
      </div>

      {loading ? (
        <div className="text-sm text-hg-text-dim py-6">Loading {stream.label}…</div>
      ) : (
      <>
      {/* Drafts + AI batch */}
      <div className="bg-hg-surface border border-hg-border rounded-sm p-4 space-y-4">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <h3 className="text-sm font-sans font-semibold text-hg-text uppercase tracking-wider flex items-center gap-2">
            <FileText size={14} className="text-hg-green" /> {stream.label} Drafts
            <span className="text-hg-text-dim font-normal">({drafts.length})</span>
          </h3>
          <div className="flex gap-2">
            <button data-testid="nlc-new-blank" onClick={newBlank}
              className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-sans bg-hg-surface border border-hg-border text-hg-text rounded-sm hover:border-hg-green">
              <Plus size={13} /> New blank
            </button>
            <button onClick={() => fetchDrafts(streamType)}
              className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-sans bg-hg-surface border border-hg-border text-hg-text-dim rounded-sm hover:border-hg-green">
              <RefreshCw size={12} /> Refresh
            </button>
          </div>
        </div>

        <div className="border border-hg-border rounded-sm p-3 space-y-2 bg-hg-bg/40">
          <div className="text-[11px] font-sans uppercase tracking-wider text-hg-green flex items-center gap-1.5">
            <Wand2 size={12} /> AI Batch — draft several {stream.label} issues at once
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <label className="text-xs font-sans text-hg-text-dim">How many:</label>
            <select value={batchCount} onChange={(e) => setBatchCount(Number(e.target.value))}
              className="bg-hg-bg border border-hg-border rounded-sm px-2 py-1.5 text-sm font-sans text-hg-text focus:border-hg-green focus:outline-none">
              {[2, 3, 4, 6].map((n) => <option key={n} value={n}>{n}</option>)}
            </select>
            <input value={batchThemes} onChange={(e) => setBatchThemes(e.target.value)}
              placeholder="optional themes (e.g. fitting science, junior golf, events)"
              className="flex-1 min-w-[200px] bg-hg-bg border border-hg-border rounded-sm px-2 py-1.5 text-sm font-sans text-hg-text focus:border-hg-green focus:outline-none" />
            <button data-testid="nlc-batch-run" onClick={runBatch} disabled={batchGenerating}
              className="flex items-center gap-1.5 px-4 py-2 text-sm font-sans bg-hg-green text-hg-text rounded-sm hover:bg-hg-green-hover disabled:opacity-40">
              {batchGenerating ? <Loader2 size={14} className="animate-spin" /> : <Wand2 size={14} />}
              {batchGenerating ? 'Generating…' : `Generate ${batchCount}`}
            </button>
          </div>
          <p className="text-[10px] text-hg-text-dim font-sans flex items-center gap-1">
            <AlertTriangle size={11} /> Drafts only — distinct issues. Review, edit & test each before scheduling.
          </p>
        </div>

        {drafts.length === 0 ? (
          <p className="text-xs text-hg-text-dim font-sans">
            No {stream.label} drafts yet. Use “AI draft this issue” or the batch generator.
          </p>
        ) : (
          <div className="space-y-1.5 max-h-72 overflow-auto">
            {drafts.map((d) => (
              <button key={d.id} data-testid={`nlc-draft-${d.id}`} onClick={() => openDraft(d)}
                className={`w-full text-left flex items-center justify-between gap-3 px-3 py-2 rounded-sm border transition-colors ${issue.id === d.id ? 'border-hg-green bg-hg-green/10' : 'border-hg-border hover:border-hg-green'}`}>
                <span className="text-sm font-sans text-hg-text truncate">
                  {d.subject || '(untitled)'}
                  {d.source === 'ai_batch' && <span className="ml-2 text-[9px] uppercase tracking-wider text-hg-green">AI</span>}
                </span>
                <span className="text-[10px] font-sans uppercase tracking-wider text-hg-text-dim shrink-0">{draftStatus(d)}</span>
              </button>
            ))}
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Editor */}
        <div className="bg-hg-surface border border-hg-border rounded-sm p-6 space-y-5">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-sans font-semibold text-hg-text uppercase tracking-wider">{stream.label} — Issue Editor</h3>
            {notice && <span className="text-xs text-hg-green font-sans">{notice}</span>}
          </div>

          {/* AI draft this issue */}
          <div className="border border-hg-green/40 rounded-sm p-3 space-y-2 bg-hg-green/5">
            <div className="text-[11px] font-sans uppercase tracking-wider text-hg-green flex items-center gap-1.5">
              <Sparkles size={12} /> AI draft this issue
            </div>
            <input value={aiBrief} onChange={(e) => setAiBrief(e.target.value)}
              placeholder="Optional brief (blank = AI decides) — e.g. reading lie angle under pressure"
              className="w-full bg-hg-bg border border-hg-border rounded-sm px-2 py-1.5 text-sm font-sans text-hg-text focus:border-hg-green focus:outline-none" />
            <div className="flex flex-wrap items-center gap-2">
              <input value={aiUpdate} onChange={(e) => setAiUpdate(e.target.value)}
                placeholder="What's New this issue (seeds the news slot)"
                className="flex-1 min-w-[220px] bg-hg-bg border border-hg-border rounded-sm px-2 py-1.5 text-sm font-sans text-hg-text focus:border-hg-green focus:outline-none" />
              <button data-testid="nlc-ai-draft" onClick={aiDraft} disabled={aiDrafting}
                className="flex items-center gap-1.5 px-4 py-2 text-sm font-sans bg-hg-green text-hg-text rounded-sm hover:bg-hg-green-hover disabled:opacity-40">
                {aiDrafting ? <Loader2 size={14} className="animate-spin" /> : <Sparkles size={14} />}
                {aiDrafting ? 'Drafting…' : 'AI draft'}
              </button>
            </div>
            <p className="text-[10px] text-hg-text-dim font-sans">
              Fills the fields below as an editable draft. Review & edit, then test — never auto-sent.
            </p>
          </div>

          {/* Backbone */}
          <Field label="Subject">
            <input className={inputCls} data-testid="nlc-subject" value={issue.subject} onChange={(e) => setField('subject', e.target.value)}
              placeholder="What changed in your fittings this month" />
          </Field>
          <Field label="Intro / dek" hint="short opening line under the title">
            <textarea rows={2} className={inputCls} data-testid="nlc-intro" value={issue.intro || ''} onChange={(e) => setField('intro', e.target.value)} />
          </Field>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Field label="Issue date" hint="shows on Content Calendar">
              <input type="date" className={inputCls} data-testid="nlc-issue-date" value={issue.issue_date || ''} onChange={(e) => setField('issue_date', e.target.value)} />
            </Field>
            <Field label="Greeting" hint="“Hi {name},” above the intro">
              <label className="flex items-center gap-2 text-sm font-sans text-hg-text">
                <input type="checkbox" data-testid="nlc-show-greeting" checked={issue.show_greeting !== false}
                  onChange={(e) => setField('show_greeting', e.target.checked)} />
                Greet the reader by name
              </label>
            </Field>
            <Field label="Date label" hint="e.g. June 2026 (blank = auto)">
              <input className={inputCls} data-testid="nlc-date-label" value={issue.date_label || ''} onChange={(e) => setField('date_label', e.target.value)} placeholder="June 2026" />
            </Field>
          </div>
          <Field label="Preheader" hint="inbox preview text (blank = auto)">
            <input className={inputCls} data-testid="nlc-preheader" value={issue.preheader || ''} onChange={(e) => setField('preheader', e.target.value)} />
          </Field>

          {/* What the Hub is holding that could go in this issue. A note, not an insertion:
              whether a film belongs in a given issue is an editorial call. */}
          {streamType === 't2t' && <VideoCandidatesNote where="t2t" />}

          {/* Hero image */}
          <div className="border-t border-hg-border pt-4">
            <EmailImagePanel testIdPrefix="nlc-hero-img" label="Hero image (top of newsletter)" context="hero"
              promptContext={issue.subject || `${stream.label} hero`} imageData={issue.hero_image} onImageSet={(v) => setField('hero_image', v)} brandToggle />
          </div>

          {/* Sections */}
          <div className="border-t border-hg-border pt-4 space-y-3">
            <div className="flex items-center justify-between">
              <div className="text-[11px] font-sans uppercase tracking-wider text-hg-green">Body sections</div>
              <div className="flex gap-2">
                <button data-testid="nlc-add-section" onClick={addStandard}
                  className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-sans bg-hg-surface border border-hg-border text-hg-text rounded-sm hover:border-hg-green">
                  <Plus size={12} /> Add section
                </button>
                {streamType === 't2t' && !hasResource && (
                  <button data-testid="nlc-add-resource" onClick={addResource}
                    className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-sans bg-hg-surface border border-hg-border text-hg-text rounded-sm hover:border-hg-green">
                    <Plus size={12} /> Add Fitter Resource
                  </button>
                )}
                {!hasReviewRequest && (
                  <button data-testid="nlc-add-review" onClick={addReviewRequest}
                    className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-sans bg-hg-surface border border-hg-border text-hg-text rounded-sm hover:border-hg-green">
                    <Plus size={12} /> Add review request
                  </button>
                )}
              </div>
            </div>
            {issue.sections.map((section, i) => (
              <SectionBlock
                key={i}
                section={section}
                index={i}
                isFirst={i <= 1}
                isLast={i === lastIdx}
                streamLabel={stream.label}
                onField={setSectionField}
                onImage={setSectionImage}
                onMove={moveSection}
                onRemove={removeSection}
              />
            ))}
          </div>

          {/* Sign-off */}
          <div className="border-t border-hg-border pt-4 space-y-4">
            <div className="text-[11px] font-sans uppercase tracking-wider text-hg-green">Sign-off</div>
            <Field label="Lead line"><input className={inputCls} data-testid="nlc-signoff-lead" value={issue.sign_off_lead || ''} onChange={(e) => setField('sign_off_lead', e.target.value)} placeholder="See you in the next issue." /></Field>
            <Field label="Signed" hint="Brand name — not a personal name">
              <input className={inputCls} data-testid="nlc-signoff" value={issue.sign_off || ''} onChange={(e) => setField('sign_off', e.target.value)} />
            </Field>
          </div>

          {/* Sending & Scheduling */}
          <div className="border-t border-hg-border pt-4 space-y-3">
            <div className="flex items-center justify-between flex-wrap gap-2">
              <span className="inline-flex items-center gap-1.5 text-[11px] font-sans px-2 py-1 rounded-sm border border-hg-border text-hg-text-dim">
                {status === 'scheduled' ? <CalendarClock size={12} className="text-hg-green" />
                  : status === 'sent' ? <CheckCircle2 size={12} className="text-hg-green" />
                  : hasTested ? <CheckCircle2 size={12} className="text-hg-green" /> : null}
                Status:&nbsp;<strong className="text-hg-text">{statusLabel(issue)}</strong>
                {hasTested && status !== 'scheduled' && status !== 'sent' && (
                  <span className="text-hg-text-dim">· tested {fmt(issue.test_sent_at)}</span>
                )}
              </span>
              <span className="inline-flex items-center gap-1.5 text-[10px] font-sans text-hg-text-dim">
                <Users size={11} /> Real target: <strong className="text-hg-text">{targetName}</strong>
                {target?.subscriber_count != null && <span>· ~{target.subscriber_count} recipients</span>}
                {target && target.configured === false && <span className="text-amber-500">· not configured</span>}
              </span>
            </div>
            <IssueNotices issue={issue} />

            <div className="flex flex-wrap gap-2">
              <button data-testid="nlc-save" onClick={save} disabled={saving}
                className="flex items-center gap-1.5 px-4 py-2 text-sm font-sans bg-hg-surface border border-hg-border text-hg-text rounded-sm hover:border-hg-green disabled:opacity-40">
                {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} Save
              </button>
              <button data-testid="nlc-preview" onClick={preview} disabled={previewing}
                className="flex items-center gap-1.5 px-4 py-2 text-sm font-sans bg-hg-surface border border-hg-border text-hg-text rounded-sm hover:border-hg-green disabled:opacity-40">
                {previewing ? <Loader2 size={14} className="animate-spin" /> : <Eye size={14} />} Preview
              </button>
              <div className="inline-flex items-center gap-1.5">
                <input type="text" value={testEmail} onChange={(e) => setTestEmail(e.target.value)}
                  placeholder="you@henry-griffitts.com, grant@…"
                  title="Test recipient(s) — separate multiple with commas (each added to HG Hub Test group)"
                  className="w-56 bg-hg-bg border border-hg-border rounded-sm px-2 py-2 text-sm font-sans text-hg-text focus:border-hg-green focus:outline-none" />
                <button data-testid="nlc-send-test" onClick={sendTest} disabled={sendingTest}
                  className="flex items-center gap-1.5 px-4 py-2 text-sm font-sans bg-hg-green text-hg-text rounded-sm hover:bg-hg-green-hover disabled:opacity-40 whitespace-nowrap">
                  {sendingTest ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />} Send test
                </button>
              </div>
              <button data-testid="nlc-schedule-toggle" onClick={() => setShowSchedule((s) => !s)} disabled={!hasTested || isScheduled}
                title={!hasTested ? 'Send a test to yourself first' : isScheduled ? 'Already scheduled' : ''}
                className="flex items-center gap-1.5 px-4 py-2 text-sm font-sans border border-hg-green text-hg-green rounded-sm hover:bg-hg-green/10 disabled:opacity-40 disabled:cursor-not-allowed">
                <CalendarClock size={14} /> Schedule…
              </button>
            </div>

            {!hasTested && (
              <p className="text-[10px] text-hg-text-dim font-sans flex items-center gap-1">
                <AlertTriangle size={11} /> Send a test to yourself before you can schedule to the list.
              </p>
            )}

            {showSchedule && hasTested && !isScheduled && (
              <div className="border border-hg-green/50 rounded-sm p-3 space-y-2 bg-hg-green/5">
                <p className="text-xs font-sans text-hg-text">
                  This will schedule a send to{' '}
                  <strong>{scheduleToTest ? 'HG Hub Test (just you)' : targetName}</strong>
                  {!scheduleToTest && target?.subscriber_count != null && <> (~{target.subscriber_count} recipients)</>} at the time below.
                  It stays visible and cancellable in MailerLite until it fires.
                </p>
                <input type="datetime-local" data-testid="nlc-schedule-at" value={sendAt} onChange={(e) => setSendAt(e.target.value)}
                  className="bg-hg-bg border border-hg-border rounded-sm px-2 py-1.5 text-sm font-sans text-hg-text focus:border-hg-green focus:outline-none" />
                <label className="flex items-start gap-2 text-[11px] font-sans text-hg-text-dim cursor-pointer">
                  <input type="checkbox" checked={scheduleToTest} onChange={(e) => setScheduleToTest(e.target.checked)} className="mt-0.5" />
                  <span>Dry-run: schedule to the <strong>HG Hub Test</strong> group (just you), not the real list — for validating the scheduler.</span>
                </label>
                <label className="flex items-start gap-2 text-xs font-sans text-hg-text cursor-pointer">
                  <input type="checkbox" data-testid="nlc-schedule-confirm" checked={confirmChecked} onChange={(e) => setConfirmChecked(e.target.checked)} className="mt-0.5" />
                  <span>I confirm scheduling a send to <strong>{scheduleToTest ? 'HG Hub Test (just you)' : targetName}</strong> at the time above.</span>
                </label>
                <div className="flex gap-2">
                  <button data-testid="nlc-schedule-confirm-btn" onClick={doSchedule} disabled={scheduling || !confirmChecked || !sendAt}
                    className="flex items-center gap-1.5 px-4 py-2 text-sm font-sans bg-hg-green text-hg-text rounded-sm hover:bg-hg-green-hover disabled:opacity-40 disabled:cursor-not-allowed">
                    {scheduling ? <Loader2 size={14} className="animate-spin" /> : <CalendarClock size={14} />}
                    {scheduleToTest ? 'Confirm & schedule (test)' : 'Confirm & schedule'}
                  </button>
                  <button onClick={() => { setShowSchedule(false); setConfirmChecked(false); }}
                    className="px-4 py-2 text-sm font-sans border border-hg-border text-hg-text-dim rounded-sm hover:border-hg-green">
                    Cancel
                  </button>
                </div>
              </div>
            )}

            {isScheduled && (
              <div className="border-2 border-hg-green rounded-sm p-4 space-y-2 bg-hg-green/10">
                <div className="flex items-center gap-2 text-sm font-sans font-semibold text-hg-text">
                  <CalendarClock size={16} className="text-hg-green" /> Scheduled
                  {issue.scheduled_test && <span className="text-[10px] font-normal text-hg-text-dim">(test dry-run)</span>}
                </div>
                <p className="text-sm font-sans text-hg-text">
                  Sending to <strong>{scheduledTargetName}</strong> on <strong>{fmt(issue.scheduled_at)}</strong>.
                </p>
                <div className="flex flex-wrap gap-2 pt-1">
                  {onNavigate && (
                    <button onClick={() => onNavigate('calendar')}
                      className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-sans bg-hg-green text-hg-text rounded-sm hover:bg-hg-green-hover">
                      <CalendarClock size={13} /> View on Content Calendar
                    </button>
                  )}
                  {mlUrl && (
                    <a href={mlUrl} target="_blank" rel="noreferrer"
                      className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-sans border border-hg-green text-hg-green rounded-sm hover:bg-hg-green/10">
                      <Eye size={13} /> View in MailerLite
                    </a>
                  )}
                  <button onClick={cancelSchedule} disabled={cancelling}
                    className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-sans border border-red-400 text-red-400 rounded-sm hover:bg-red-400/10 disabled:opacity-40">
                    {cancelling ? <Loader2 size={13} className="animate-spin" /> : <Ban size={13} />} Cancel schedule
                  </button>
                </div>
                <p className="text-[10px] text-hg-text-dim font-sans">
                  Also visible and cancellable in MailerLite until it sends.
                </p>
              </div>
            )}

            <p className="text-[10px] text-hg-text-dim font-sans flex items-center gap-1">
              <AlertTriangle size={11} /> <strong>Scheduling</strong> sends automatically at the chosen time — allowed only after a test + explicit confirm.
            </p>
          </div>
        </div>

        {/* Preview */}
        <div className="bg-hg-surface border border-hg-border rounded-sm p-3">
          {previewHtml ? (
            <iframe title="newsletter-preview" srcDoc={previewHtml} className="w-full h-[80vh] bg-white rounded-sm border border-hg-border" />
          ) : (
            <div className="h-[80vh] flex items-center justify-center text-sm text-hg-text-dim font-sans">
              Click <span className="text-hg-text mx-1">Preview</span> to render the email
            </div>
          )}
        </div>
      </div>
      </>
      )}
    </div>
  );
}
